import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { CursorPosition } from './DemoAnimationOverlay';

// ── Phase 1 text ──────────────────────────────────────────────────────────────

const APPROVAL_NOTES_TEXT: string =
    'Head in through the northeast entrance, Worky will check you in as soon as you arrive.';

// ── Phase 2 text ──────────────────────────────────────────────────────────────

const NEW_SHIFT_INSTRUCTIONS_TEXT: string =
    'Report to the main physio unit on arrival and check in with the lead therapist. Patient files will be ready for you.';

// ── Shared timing constants ───────────────────────────────────────────────────

const TYPING_DELAY_MS: number = 22;
const CURSOR_MOVE_MS: number = 560;

// ── Screen enum ───────────────────────────────────────────────────────────────

enum ActiveDemoScreen {
    SHIFTS = 'shifts',
    WORKFORCE = 'workforce',
    PAYROLL = 'payroll',
    INVOICES = 'invoices',
}

// ── Combined animation state ──────────────────────────────────────────────────

interface DemoAnimState {
    // Shared overlay
    readonly isVisible: boolean;
    readonly cursorPos: CursorPosition;
    readonly cursorTransitionMs: number;
    readonly isClicking: boolean;

    // Nav / screen routing
    readonly activeScreen: ActiveDemoScreen;
    readonly activeNavItem: string;

    // Phase 1 — Shift Approval (Priya Patel)
    readonly showApprovalPopup: boolean;
    readonly approvalTypedNotes: string;
    readonly isApprovalNotesFocused: boolean;
    readonly showApprovalSuccess: boolean;

    // Phase 2 — New Shift Creation (David Chen)
    readonly showNewShiftPopup: boolean;
    readonly isTimeDropdownOpen: boolean;
    readonly selectedTime: string;
    readonly isOrgDropdownOpen: boolean;
    readonly selectedOrg: string;
    readonly newShiftTypedInstructions: string;
    readonly isInstructionsFocused: boolean;
    readonly showNewShiftSuccess: boolean;

    // Phase 3b — Worker Profile (David Chen)
    readonly showWorkerProfilePanel: boolean;
    readonly wpIsEditingRate: boolean;
    readonly wpEditRateText: string;
    readonly wpFirstRateDisplay: string;
    readonly wpDeductionCount: number;

    // Phase 4 — Worky Pay payroll screen
    readonly payrollCycleExpanded: boolean;

    // Phase 5 — Client Invoices
    readonly isKingstonExpanded: boolean;
    readonly showInvoicePdf: boolean;
    readonly invoiceSentSuccess: boolean;
}

const INITIAL_STATE: DemoAnimState = {
    isVisible: false,
    cursorPos: { x: 78, y: 9 },
    cursorTransitionMs: 0,
    isClicking: false,

    activeScreen: ActiveDemoScreen.SHIFTS,
    activeNavItem: 'shift-management',

    showApprovalPopup: false,
    approvalTypedNotes: '',
    isApprovalNotesFocused: false,
    showApprovalSuccess: false,

    showNewShiftPopup: false,
    isTimeDropdownOpen: false,
    selectedTime: '',
    isOrgDropdownOpen: false,
    selectedOrg: '',
    newShiftTypedInstructions: '',
    isInstructionsFocused: false,
    showNewShiftSuccess: false,

    showWorkerProfilePanel: false,
    wpIsEditingRate: false,
    wpEditRateText: '',
    wpFirstRateDisplay: '$78/hr',
    wpDeductionCount: 0,

    payrollCycleExpanded: false,

    isKingstonExpanded: false,
    showInvoicePdf: false,
    invoiceSentSuccess: false,
};

/**
 * Two-phase looping demo animation:
 *   Phase 1 — approves Priya Patel's active shift
 *   Phase 2 — creates a new shift on David Chen's empty cell
 *
 * Required data-demo-target attributes in the container:
 *   Phase 1:  priya-active-shift | approval-notes-textarea | approval-approve-btn
 *   Phase 2:  david-empty-cell   | nsp-time-trigger | nsp-time-opt-selected
 *             nsp-org-trigger    | nsp-org-opt-selected
 *             nsp-instructions   | nsp-send-btn
 */
const useShiftApprovalDemoAnimation = (
    containerRef: RefObject<HTMLDivElement>,
    isAnimationEnabled: boolean
): DemoAnimState => {
    const [animState, setAnimState] = useState<DemoAnimState>(INITIAL_STATE);
    const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
    const phase1Ref = useRef<() => void>();
    const phase2Ref = useRef<() => void>();
    const phase3Ref = useRef<() => void>();
    const phase4Ref = useRef<() => void>();
    const phase5Ref = useRef<() => void>();

    const getTargetCenter = useCallback(
        (targetId: string): CursorPosition => {
            const container: HTMLDivElement | null = containerRef.current;
            if (!container) return { x: 50, y: 50 };

            const target: Element | null = container.querySelector(
                `[data-demo-target="${targetId}"]`
            );
            if (!target) return { x: 50, y: 50 };

            const cr: DOMRect = container.getBoundingClientRect();
            const tr: DOMRect = target.getBoundingClientRect();

            return {
                x: ((tr.left + tr.width / 2 - cr.left) / cr.width) * 100,
                y: ((tr.top + tr.height / 2 - cr.top) / cr.height) * 100,
            };
        },
        [containerRef]
    );

    useEffect(() => {
        const clearAll = (): void => {
            timeoutsRef.current.forEach(clearTimeout);
            timeoutsRef.current = [];
        };

        const schedule = (fn: () => void, ms: number): void => {
            const id: ReturnType<typeof setTimeout> = setTimeout(fn, ms);
            timeoutsRef.current.push(id);
        };

        if (!isAnimationEnabled) {
            clearAll();
            setAnimState(INITIAL_STATE);
            return () => clearAll();
        }

        // ─────────────────────────────────────────────────────────────────────
        // PHASE 1 — Approve Priya's active shift
        // ─────────────────────────────────────────────────────────────────────
        const runPhase1 = (): void => {
            clearAll();
            let t: number = 0;

            // Reset everything
            setAnimState(INITIAL_STATE);

            // Fade in overlay
            t += 450;
            schedule(() => setAnimState(prev => ({ ...prev, isVisible: true })), t);

            // Enable transition + move cursor to Priya's active shift
            t += 280;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('priya-active-shift');
                setAnimState(prev => ({ ...prev, cursorTransitionMs: CURSOR_MOVE_MS, cursorPos: pos }));
            }, t);

            // Click shift block
            t += CURSOR_MOVE_MS + 60;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: false })), t);

            // Approval popup appears
            t += 90;
            schedule(() => setAnimState(prev => ({ ...prev, showApprovalPopup: true })), t);

            // Move cursor to notes textarea
            t += 520;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('approval-notes-textarea');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            // Click in textarea
            t += CURSOR_MOVE_MS + 55;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: false, isApprovalNotesFocused: true })), t);

            // Type notes character by character
            t += 120;
            const approvalTypingStart: number = t;
            APPROVAL_NOTES_TEXT.split('').forEach((_c: string, i: number) => {
                const count: number = i + 1;
                schedule(
                    () => setAnimState(prev => ({ ...prev, approvalTypedNotes: APPROVAL_NOTES_TEXT.slice(0, count) })),
                    approvalTypingStart + count * TYPING_DELAY_MS
                );
            });

            const afterApprovalTyping: number = approvalTypingStart + APPROVAL_NOTES_TEXT.length * TYPING_DELAY_MS;

            // Move cursor to Approve button
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('approval-approve-btn');
                setAnimState(prev => ({ ...prev, isApprovalNotesFocused: false, cursorPos: pos }));
            }, afterApprovalTyping + 360);

            // Click Approve
            schedule(
                () => setAnimState(prev => ({ ...prev, isClicking: true })),
                afterApprovalTyping + 360 + CURSOR_MOVE_MS + 55
            );
            schedule(
                () => setAnimState(prev => ({ ...prev, isClicking: false })),
                afterApprovalTyping + 360 + CURSOR_MOVE_MS + 290
            );

            // Show success
            const approvalSuccessAt: number = afterApprovalTyping + 360 + CURSOR_MOVE_MS + 380;
            schedule(() => setAnimState(prev => ({ ...prev, showApprovalSuccess: true })), approvalSuccessAt);

            // Fade out overlay then hand off to Phase 2
            schedule(() => setAnimState(prev => ({ ...prev, isVisible: false })), approvalSuccessAt + 2200);
            schedule(() => phase2Ref.current?.(), approvalSuccessAt + 2200 + 700);
        };

        // ─────────────────────────────────────────────────────────────────────
        // PHASE 2 — Create new shift on David Chen's empty cell
        // ─────────────────────────────────────────────────────────────────────
        const runPhase2 = (): void => {
            clearAll();
            let t: number = 0;

            // Reset to clean state
            setAnimState(INITIAL_STATE);

            // Fade in overlay
            t += 450;
            schedule(() => setAnimState(prev => ({ ...prev, isVisible: true })), t);

            // Move cursor to David Chen's empty cell
            t += 280;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('david-empty-cell');
                setAnimState(prev => ({ ...prev, cursorTransitionMs: CURSOR_MOVE_MS, cursorPos: pos }));
            }, t);

            // Click the cell
            t += CURSOR_MOVE_MS + 60;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: false })), t);

            // New shift popup appears
            t += 90;
            schedule(() => setAnimState(prev => ({ ...prev, showNewShiftPopup: true })), t);

            // ── Select time ───────────────────────────────────────────────────

            // Move cursor to time dropdown trigger
            t += 480;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('nsp-time-trigger');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            // Click trigger → open dropdown
            t += CURSOR_MOVE_MS + 55;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: false, isTimeDropdownOpen: true })), t);

            // Move cursor to the '9AM – 5PM' option
            t += 260;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('nsp-time-opt-selected');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            // Click option → select time, close dropdown
            t += CURSOR_MOVE_MS + 55;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                isTimeDropdownOpen: false,
                selectedTime: '9AM – 5PM',
            })), t);

            // ── Select organisation ───────────────────────────────────────────

            // Move cursor to org dropdown trigger
            t += 280;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('nsp-org-trigger');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            // Click trigger → open org dropdown
            t += CURSOR_MOVE_MS + 55;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: false, isOrgDropdownOpen: true })), t);

            // Move cursor to 'Kingston Health SC' option
            t += 260;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('nsp-org-opt-selected');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            // Click option → select org, close dropdown
            t += CURSOR_MOVE_MS + 55;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                isOrgDropdownOpen: false,
                selectedOrg: 'Kingston Health SC',
            })), t);

            // ── Type instructions ─────────────────────────────────────────────

            // Move cursor to instructions field
            t += 280;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('nsp-instructions');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            // Click instructions field
            t += CURSOR_MOVE_MS + 55;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: false, isInstructionsFocused: true })), t);

            // Type
            t += 120;
            const instrTypingStart: number = t;
            NEW_SHIFT_INSTRUCTIONS_TEXT.split('').forEach((_c: string, i: number) => {
                const count: number = i + 1;
                schedule(
                    () => setAnimState(prev => ({
                        ...prev,
                        newShiftTypedInstructions: NEW_SHIFT_INSTRUCTIONS_TEXT.slice(0, count),
                    })),
                    instrTypingStart + count * TYPING_DELAY_MS
                );
            });

            const afterInstrTyping: number = instrTypingStart + NEW_SHIFT_INSTRUCTIONS_TEXT.length * TYPING_DELAY_MS;

            // ── Send Shift ────────────────────────────────────────────────────

            // Move cursor to Send Shift button
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('nsp-send-btn');
                setAnimState(prev => ({ ...prev, isInstructionsFocused: false, cursorPos: pos }));
            }, afterInstrTyping + 350);

            // Click Send Shift
            schedule(
                () => setAnimState(prev => ({ ...prev, isClicking: true })),
                afterInstrTyping + 350 + CURSOR_MOVE_MS + 55
            );
            schedule(
                () => setAnimState(prev => ({ ...prev, isClicking: false })),
                afterInstrTyping + 350 + CURSOR_MOVE_MS + 290
            );

            // Show success
            const newShiftSuccessAt: number = afterInstrTyping + 350 + CURSOR_MOVE_MS + 380;
            schedule(() => setAnimState(prev => ({ ...prev, showNewShiftSuccess: true })), newShiftSuccessAt);

            // Fade out then hand off to Phase 3
            schedule(() => setAnimState(prev => ({ ...prev, isVisible: false })), newShiftSuccessAt + 2200);
            schedule(() => phase3Ref.current?.(), newShiftSuccessAt + 2200 + 700);
        };

        // ─────────────────────────────────────────────────────────────────────
        // PHASE 3 — Navigate to Workforce → open David Chen's profile →
        //           edit rate, add deductions → close → return to Shift Mgmt
        // ─────────────────────────────────────────────────────────────────────
        const NEW_RATE_TEXT: string = '85'; // typing "85" into the rate field

        const runPhase3 = (): void => {
            clearAll();
            let t: number = 0;

            setAnimState(INITIAL_STATE);

            // ── Fade in + navigate to Workforce ──────────────────────────
            t += 450;
            schedule(() => setAnimState(prev => ({ ...prev, isVisible: true })), t);

            t += 280;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('nav-workforce');
                setAnimState(prev => ({ ...prev, cursorTransitionMs: CURSOR_MOVE_MS, cursorPos: pos }));
            }, t);

            t += CURSOR_MOVE_MS + 60;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                activeScreen: ActiveDemoScreen.WORKFORCE,
                activeNavItem: 'workforce',
            })), t);

            // ── Hover Priya's row (narrative: check she's now On Shift) ──
            t += 420;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('workforce-row-priya');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            // ── Move to David's row and click to open his profile ─────────
            t += CURSOR_MOVE_MS + 680;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('workforce-row-david');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            t += CURSOR_MOVE_MS + 60;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: false })), t);

            // Worker profile panel slides in
            t += 120;
            schedule(() => setAnimState(prev => ({ ...prev, showWorkerProfilePanel: true })), t);

            // ── Move to edit button on first worker type ──────────────────
            t += 520;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('wp-edit-btn-physio');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            t += CURSOR_MOVE_MS + 60;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                wpIsEditingRate: true,
            })), t);

            // ── Click inside the rate input field ────────────────────────
            t += 320;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('wp-rate-input');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            t += CURSOR_MOVE_MS + 60;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: false })), t);

            // ── Type new rate character by character ─────────────────────
            t += 120;
            const rateTypingStart: number = t;
            NEW_RATE_TEXT.split('').forEach((_c: string, i: number) => {
                const count: number = i + 1;
                schedule(
                    () => setAnimState(prev => ({
                        ...prev,
                        wpEditRateText: NEW_RATE_TEXT.slice(0, count),
                    })),
                    rateTypingStart + count * TYPING_DELAY_MS
                );
            });

            const afterRateTyping: number = rateTypingStart + NEW_RATE_TEXT.length * TYPING_DELAY_MS;

            // ── Move to Save button and click ────────────────────────────
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('wp-save-rate-btn');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, afterRateTyping + 300);

            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), afterRateTyping + 300 + CURSOR_MOVE_MS + 55);
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                wpIsEditingRate: false,
                wpFirstRateDisplay: '$85/hr',
            })), afterRateTyping + 300 + CURSOR_MOVE_MS + 290);

            const afterSave: number = afterRateTyping + 300 + CURSOR_MOVE_MS + 290;

            // ── Move to + Add deduction button ───────────────────────────
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('wp-add-deduction-btn');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, afterSave + 400);

            // First click — parking deduction
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), afterSave + 400 + CURSOR_MOVE_MS + 55);
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                wpDeductionCount: 1,
            })), afterSave + 400 + CURSOR_MOVE_MS + 290);

            const afterDeduction1: number = afterSave + 400 + CURSOR_MOVE_MS + 290;

            // Second click — meal allowance deduction (cursor stays near button)
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), afterDeduction1 + 680);
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                wpDeductionCount: 2,
            })), afterDeduction1 + 680 + 240);

            const afterDeduction2: number = afterDeduction1 + 680 + 240;

            // ── Move to Close button ─────────────────────────────────────
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('wp-close-btn');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, afterDeduction2 + 600);

            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), afterDeduction2 + 600 + CURSOR_MOVE_MS + 55);
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                showWorkerProfilePanel: false,
            })), afterDeduction2 + 600 + CURSOR_MOVE_MS + 290);

            const afterClose: number = afterDeduction2 + 600 + CURSOR_MOVE_MS + 290;

            // ── Fade out and hand off directly to Phase 4 (Worky Pay) ────
            schedule(() => setAnimState(prev => ({ ...prev, isVisible: false })), afterClose + 400);
            schedule(() => phase4Ref.current?.(), afterClose + 400 + 700);
        };

        // ─────────────────────────────────────────────────────────────────────
        // PHASE 4 — Navigate to Worky Pay → expand Apr 1–15 cycle →
        //           spotlight David Chen's updated pay stub → return
        // ─────────────────────────────────────────────────────────────────────
        const runPhase4 = (): void => {
            clearAll();
            let t: number = 0;

            // Start on the Workforce screen (Phase 3 left it there)
            setAnimState({
                ...INITIAL_STATE,
                activeScreen: ActiveDemoScreen.WORKFORCE,
                activeNavItem: 'workforce',
            });

            // ── Fade in overlay ───────────────────────────────────────────
            t += 450;
            schedule(() => setAnimState(prev => ({ ...prev, isVisible: true })), t);

            // ── Move cursor to "Worky Pay" nav item ───────────────────────
            t += 280;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('nav-worky-pay');
                setAnimState(prev => ({ ...prev, cursorTransitionMs: CURSOR_MOVE_MS, cursorPos: pos }));
            }, t);

            // Click "Worky Pay"
            t += CURSOR_MOVE_MS + 60;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                activeScreen: ActiveDemoScreen.PAYROLL,
                activeNavItem: 'worky-pay',
            })), t);

            // ── Move cursor to the Apr 1–Apr 15 pay cycle row ─────────────
            t += 520;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('payroll-cycle-apr1');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            // Click to expand
            t += CURSOR_MOVE_MS + 60;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                payrollCycleExpanded: true,
            })), t);

            // ── Move cursor to David Chen's highlighted row ───────────────
            t += 380;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('payroll-david-row');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            // Hover on David's row for 2.8 s so the viewer can read the details
            t += CURSOR_MOVE_MS + 2800;

            // ── Fade out and hand off to Phase 5 (Client Invoices) ───────
            t += 400;
            schedule(() => setAnimState(prev => ({ ...prev, isVisible: false })), t);
            schedule(() => phase5Ref.current?.(), t + 700);
        };

        // ─────────────────────────────────────────────────────────────────────
        // PHASE 5 — Navigate to Client Invoices → expand Kingston Health SC →
        //           click most recent invoice → view PDF side panel → return
        // ─────────────────────────────────────────────────────────────────────
        const runPhase5 = (): void => {
            clearAll();
            let t: number = 0;

            // Start on the Payroll screen (Phase 4 left it there)
            setAnimState({
                ...INITIAL_STATE,
                activeScreen: ActiveDemoScreen.PAYROLL,
                activeNavItem: 'worky-pay',
                payrollCycleExpanded: true,
            });

            // ── Fade in overlay ───────────────────────────────────────────
            t += 450;
            schedule(() => setAnimState(prev => ({ ...prev, isVisible: true, cursorTransitionMs: 0 })), t);

            // ── Move cursor to "Client Invoices" nav item ─────────────────
            t += 280;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('nav-client-invoices');
                setAnimState(prev => ({ ...prev, cursorTransitionMs: CURSOR_MOVE_MS, cursorPos: pos }));
            }, t);

            // Click "Client Invoices"
            t += CURSOR_MOVE_MS + 60;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                activeScreen: ActiveDemoScreen.INVOICES,
                activeNavItem: 'client-invoices',
                payrollCycleExpanded: false,
            })), t);

            // ── Move cursor to Kingston's expand button ───────────────────
            t += 520;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('invoice-kingston-expand');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            // Click to expand Kingston accordion
            t += CURSOR_MOVE_MS + 60;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                isKingstonExpanded: true,
            })), t);

            // ── Move cursor to the most recent invoice row ────────────────
            t += 400;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('invoice-kingston-latest');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            // Click to open the PDF panel
            t += CURSOR_MOVE_MS + 60;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                showInvoicePdf: true,
            })), t);

            // ── Move cursor to "Send Invoice to Client" button ───────────
            t += 500;
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('invoice-send-btn');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            // Hover briefly so viewer can read the button
            t += CURSOR_MOVE_MS + 700;

            // Click the send button
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                invoiceSentSuccess: true,
            })), t);

            // Hold the success animation for 2.8 s
            t += 2800;

            // ── Navigate back to Shift Management ────────────────────────
            schedule(() => {
                const pos: CursorPosition = getTargetCenter('nav-shift-management');
                setAnimState(prev => ({ ...prev, cursorPos: pos }));
            }, t);

            t += CURSOR_MOVE_MS + 60;
            schedule(() => setAnimState(prev => ({ ...prev, isClicking: true })), t);
            t += 240;
            schedule(() => setAnimState(prev => ({
                ...prev,
                isClicking: false,
                activeScreen: ActiveDemoScreen.SHIFTS,
                activeNavItem: 'shift-management',
                isKingstonExpanded: false,
                showInvoicePdf: false,
                invoiceSentSuccess: false,
            })), t);

            // ── Fade out and loop back to Phase 1 ────────────────────────
            t += 400;
            schedule(() => setAnimState(prev => ({ ...prev, isVisible: false })), t);
            schedule(() => phase1Ref.current?.(), t + 700);
        };

        // Wire up the phase chain
        phase1Ref.current = runPhase1;
        phase2Ref.current = runPhase2;
        phase3Ref.current = runPhase3;
        phase4Ref.current = runPhase4;
        phase5Ref.current = runPhase5;

        // Kick off Phase 1
        runPhase1();

        return () => clearAll();
    }, [getTargetCenter, isAnimationEnabled]);

    return animState;
};

export { useShiftApprovalDemoAnimation, ActiveDemoScreen };
export type { DemoAnimState };
